import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Shield, Moon, Sun } from "lucide-react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function BotBubble({ text, options, onOption }) {
  return (
    <div className="flex justify-start chat-msg-enter">
      <div className="max-w-[85%] px-4 py-3 bg-white border border-slate-100 rounded-2xl rounded-tl-none shadow-md">
        <p className="text-sm leading-relaxed text-slate-800 font-medium whitespace-pre-line">{text}</p>
        {options && options.length > 0 && (
          <div className="mt-3 flex flex-col gap-2">
            {options.map((opt) => (
              <button
                key={opt.value}
                data-testid={`chat-opt-${opt.value.toLowerCase().replace(/[\s\/+#.,'()]/g, '-')}`}
                onClick={() => onOption(opt.label, opt.value)}
                className="w-full text-left px-4 py-2.5 rounded-xl text-sm font-bold bg-blue-50 text-blue-700 hover:bg-blue-100 flex justify-between items-center transition-all"
              >
                {opt.label} <span className="text-blue-400">&rarr;</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UserBubble({ text }) {
  return (
    <div className="flex justify-end chat-msg-enter">
      <div className="max-w-[85%] px-4 py-3 bg-blue-600 text-white rounded-2xl rounded-tr-none shadow-sm">
        <p className="text-sm font-medium whitespace-pre-line">{text}</p>
      </div>
    </div>
  );
}

export default function ChatbotPlaceholder() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(false);
  const [messages, setMessages] = useState([]);
  const [opts, setOpts] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [done, setDone] = useState(false);
  const endRef = useRef(null);

  const s = useRef({
    phase: "welcome",
    config: null,
    contact: { name: "", dob: "", address: "", email: "", phone: "", marital: "", spouse_name: "", spouse_dob: "", spouse_contact: "" },
    selectedCategory: null,
    completedCategories: [],
    questions: [],
    qIdx: 0,
    answers: {},
    allAnswers: {},
    repeatCounts: {},
    repeatIter: {},
    groupRefs: {},
  }).current;

  const scroll = () => endRef.current?.scrollIntoView({ behavior: "smooth" });
  useEffect(scroll, [messages, opts]);

  const bot = useCallback((text, options = []) => {
    setMessages((p) => [...p, { type: "bot", text }]);
    setOpts(options);
  }, []);

  const addUser = useCallback((text) => {
    setMessages((p) => [...p, { type: "user", text }]);
    setOpts([]);
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chatbot/config`);
      s.config = res.data.categories || [];
    } catch (e) {
      console.error("Failed to load chatbot config:", e);
      s.config = [];
    }
  }, [s]);

  // Wait for config before showing categories
  const ensureConfig = async () => {
    if (s.config !== null) return;
    await loadConfig();
  };

  // --- Dynamic question engine ---

  const getConditionAnswer = (questionId, currentQ) => {
    if (currentQ.repeat_group) {
      const refQ = s.questions.find((q) => q.id === questionId);
      if (refQ && refQ.repeat_group === currentQ.repeat_group) {
        const iter = s.repeatIter[currentQ.repeat_group] || 1;
        return s.answers[`${questionId}_${iter}`];
      }
    }
    return s.answers[questionId];
  };

  const checkCondition = (q) => {
    if (!q.condition) return true;
    const answer = getConditionAnswer(q.condition.question_id, q);
    if (answer === undefined || answer === null) return false;
    return q.condition.match_values.some((v) =>
      q.condition.case_insensitive !== false
        ? v.toLowerCase() === String(answer).toLowerCase()
        : v === String(answer)
    );
  };

  const findNextQ = (startIdx) => {
    let idx = startIdx;
    while (idx < s.questions.length) {
      const q = s.questions[idx];
      if (q.repeat_group) {
        const count = s.repeatCounts[q.repeat_group] || 0;
        const iter = s.repeatIter[q.repeat_group] || 1;
        if (count === 0 || iter > count) {
          while (idx < s.questions.length && s.questions[idx].repeat_group === q.repeat_group) idx++;
          continue;
        }
      }
      if (!checkCondition(q)) { idx++; continue; }
      return idx;
    }
    return -1;
  };

  const interpolate = (text, q) => {
    let result = text;
    if (q.repeat_group) {
      const ref = s.groupRefs[q.repeat_group];
      result = result.replace(/\{group_ref\}/g, ref || "their");
      const iter = s.repeatIter[q.repeat_group] || 1;
      result = result.replace(/\{item_number\}/g, String(iter));
      if (q.repeat_group_label) {
        result = result.replace(/\{item_label\}/g, `${q.repeat_group_label} ${iter}`);
      }
    }
    return result;
  };

  const askCurrentQ = () => {
    const q = s.questions[s.qIdx];
    let prefix = "";
    if (q.repeat_group && q.repeat_group_label) {
      const iter = s.repeatIter[q.repeat_group] || 1;
      if (!s.groupRefs[q.repeat_group]) {
        prefix = iter === 1
          ? `Let's collect info for ${q.repeat_group_label} ${iter}.\n`
          : `Now let's get info for ${q.repeat_group_label} ${iter}. `;
      }
    }
    const text = prefix + interpolate(q.text, q);
    bot(text, q.input_type === "options" ? q.options : []);
  };

  const advance = () => {
    const curQ = s.questions[s.qIdx];
    let nextIdx = s.qIdx + 1;
    if (curQ.repeat_group) {
      const nextQ = nextIdx < s.questions.length ? s.questions[nextIdx] : null;
      const leavingGroup = !nextQ || nextQ.repeat_group !== curQ.repeat_group;
      if (leavingGroup) {
        const count = s.repeatCounts[curQ.repeat_group] || 0;
        const iter = s.repeatIter[curQ.repeat_group] || 1;
        if (iter < count) {
          s.repeatIter[curQ.repeat_group] = iter + 1;
          s.groupRefs[curQ.repeat_group] = null;
          const firstInGroup = s.questions.findIndex((q) => q.repeat_group === curQ.repeat_group);
          nextIdx = firstInGroup;
        }
      }
    }
    const validIdx = findNextQ(nextIdx);
    if (validIdx === -1) {
      // Category questions done - go to BUNDLING phase
      s.allAnswers[s.selectedCategory.value] = { ...s.answers };
      s.completedCategories.push(s.selectedCategory.id);
      askBundling();
    } else {
      s.qIdx = validIdx;
      askCurrentQ();
    }
  };

  // --- Phase 3: Bundling ---

  const askBundling = async () => {
    await ensureConfig();
    const remaining = (s.config || []).filter(c => !s.completedCategories.includes(c.id));
    if (remaining.length === 0) {
      finishChat();
      return;
    }
    s.phase = "bundling";
    bot(
      `Great work, ${s.contact.name}! Would you like to bundle another policy for a multi-policy discount?`,
      [
        { label: "Yes, add another policy", value: "yes" },
        { label: "No, I'm all set", value: "no" },
      ]
    );
  };

  // --- Lead submission ---

  const submitLead = async () => {
    const parts = [];
    parts.push(`DOB: ${s.contact.dob}`);
    parts.push(`Address: ${s.contact.address}`);
    parts.push(`Marital: ${s.contact.marital}`);
    if (s.contact.marital === "Married") {
      parts.push(`Spouse: ${s.contact.spouse_name}, DOB: ${s.contact.spouse_dob}, Contact: ${s.contact.spouse_contact}`);
    }
    const catNames = s.completedCategories.map(id => {
      const c = (s.config || []).find(cat => cat.id === id);
      return c ? c.name : id;
    });
    parts.push(`Categories: ${catNames.join(", ")}`);
    Object.entries(s.allAnswers).forEach(([cat, answers]) => {
      parts.push(`--- ${cat} ---`);
      Object.entries(answers).forEach(([k, v]) => parts.push(`${k}: ${v}`));
    });
    parts.push("Source: Chatbot");
    try {
      await axios.post(`${API}/leads`, {
        name: s.contact.name,
        email: s.contact.email,
        phone: s.contact.phone,
        insurance_type: catNames.join(", "),
        message: parts.join("\n"),
      });
    } catch (e) { console.error(e); }
  };

  const finishChat = () => {
    bot(`Thank you, ${s.contact.name}! Your full exhaustive profile is being transmitted to the A2Insure Underwriting Desk. A specialist will review these details and contact you at ${s.contact.phone} with the final quote.`);
    setDone(true);
    s.phase = "complete";
    submitLead();
  };

  // --- Start a category ---

  const startCategory = (cat) => {
    s.selectedCategory = cat;
    s.questions = [...cat.questions].sort((a, b) => a.order - b.order);
    s.qIdx = 0;
    s.answers = {};
    s.repeatCounts = {};
    s.repeatIter = {};
    s.groupRefs = {};
    s.phase = "questions";

    const validIdx = findNextQ(0);
    if (validIdx === -1) { askBundling(); return; }
    s.qIdx = validIdx;

    if (cat.intro_message) {
      const firstQ = s.questions[validIdx];
      const qText = interpolate(firstQ.text, firstQ);
      bot(`${cat.intro_message}\n\n${qText}`, firstQ.input_type === "options" ? firstQ.options : []);
    } else {
      askCurrentQ();
    }
  };

  const showCategorySelection = async () => {
    await ensureConfig();
    const available = (s.config || []).filter(c => !s.completedCategories.includes(c.id));
    if (available.length === 0) {
      finishChat();
      return;
    }
    s.phase = "category";
    bot("Which coverage are we starting with?",
      available.map((c) => ({ label: c.name, value: c.id }))
    );
  };

  // --- Main handler ---

  const go = (val, label) => {
    const d = label || val;

    switch (s.phase) {
      case "intent":
        addUser(d);
        if (val === "quote") {
          s.phase = "contact-name";
          bot("Great! Let's get started. What is your Full Name? (First and Last)");
        } else {
          s.phase = "svc-name";
          bot("I'll connect you with a service agent. What is your name?");
        }
        break;

      // --- PHASE 1: CORE CONTACT & IDENTITY ---

      case "contact-name":
        addUser(val); s.contact.name = val;
        s.phase = "contact-dob";
        bot(`Nice to meet you, ${val}! What is your Date of Birth? (MM/DD/YYYY)`);
        break;

      case "contact-dob":
        addUser(val); s.contact.dob = val;
        s.phase = "contact-address";
        bot("What is your home address? (Street, City, State, Zip)");
        break;

      case "contact-address":
        addUser(val); s.contact.address = val;
        s.phase = "contact-email";
        bot("What is your email address? (For quote delivery)");
        break;

      case "contact-email":
        addUser(val); s.contact.email = val;
        s.phase = "contact-phone";
        bot("And your phone number? (For underwriting follow-up)");
        break;

      case "contact-phone":
        addUser(val); s.contact.phone = val;
        s.phase = "contact-marital";
        bot("What is your marital status?", [
          { label: "Single", value: "Single" },
          { label: "Married", value: "Married" },
          { label: "Divorced", value: "Divorced" },
          { label: "Widowed", value: "Widowed" },
        ]);
        break;

      case "contact-marital":
        addUser(d); s.contact.marital = val;
        if (val === "Married") {
          s.phase = "contact-spouse-name";
          bot("Please provide your spouse's Full Name:");
        } else {
          s.phase = "loading-config";
          showCategorySelection();
        }
        break;

      case "contact-spouse-name":
        addUser(val); s.contact.spouse_name = val;
        s.phase = "contact-spouse-dob";
        bot(`What is ${val}'s Date of Birth? (MM/DD/YYYY)`);
        break;

      case "contact-spouse-dob":
        addUser(val); s.contact.spouse_dob = val;
        s.phase = "contact-spouse-contact";
        bot(`What is ${s.contact.spouse_name}'s primary contact information? (Phone or Email)`);
        break;

      case "contact-spouse-contact":
        addUser(val); s.contact.spouse_contact = val;
        bot(`Got it. Thanks for those details, ${s.contact.name}.`);
        s.phase = "loading-config";
        setTimeout(() => showCategorySelection(), 300);
        break;

      // --- CATEGORY SELECTION ---

      case "category": {
        addUser(d);
        const cat = (s.config || []).find((c) => c.id === val);
        if (!cat) { bot("Please select a valid category."); return; }
        startCategory(cat);
        break;
      }

      // --- PHASE 2: QUESTIONS ---

      case "questions": {
        addUser(d);
        const q = s.questions[s.qIdx];
        const key = q.repeat_group
          ? `${q.id}_${s.repeatIter[q.repeat_group] || 1}`
          : q.id;
        s.answers[key] = val;

        if (q.sets_repeat_for) {
          s.repeatCounts[q.sets_repeat_for] = parseInt(val) || 1;
          s.repeatIter[q.sets_repeat_for] = 1;
        }
        if (q.repeat_group && q.repeat_group_label) {
          s.groupRefs[q.repeat_group] = val;
        }
        advance();
        break;
      }

      // --- PHASE 3: BUNDLING ---

      case "bundling":
        addUser(d);
        if (val === "yes") {
          s.phase = "loading-config";
          showCategorySelection();
        } else {
          finishChat();
        }
        break;

      // --- SERVICE PATH ---

      case "svc-name":
        addUser(val); s.contact.name = val;
        s.phase = "svc-issue";
        bot(`Thanks, ${val}. How can we help you today?`, [
          { label: "Pay My Bill", value: "Pay Bill" },
          { label: "File a Claim", value: "File Claim" },
          { label: "Update My Policy", value: "Update Policy" },
          { label: "Other Question", value: "Other" },
        ]);
        break;

      case "svc-issue":
        addUser(d);
        bot(`Got it, ${s.contact.name}. Our team will reach out to help with "${d}". You can also:\n\nCall: (219) 779-7040\nEmail: customerservice@a2insure.com\n\nWe'll be in touch soon!`);
        setDone(true); s.phase = "complete";
        axios.post(`${API}/contact`, {
          name: s.contact.name, email: "pending@a2insure.com",
          subject: `Service: ${d}`, message: `Chatbot service request: ${d}`
        }).catch(() => {});
        break;

      default: break;
    }
  };

  // --- UI handlers ---

  const handleOpen = () => {
    setOpen(true);
    loadConfig();
    if (messages.length === 0) {
      setTimeout(() => {
        setMessages([{ type: "bot", text: "Hi! I'm the A2Insure Digital Assistant." }]);
        setTimeout(() => {
          s.phase = "intent";
          setMessages((p) => [...p, {
            type: "bot",
            text: "Are you looking for a new quote today, or do you need help with an existing policy?"
          }]);
          setOpts([
            { label: "Get a New Quote", value: "quote" },
            { label: "Policy Help", value: "service" },
          ]);
        }, 400);
      }, 200);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const v = inputValue.trim();
    if (!v || s.phase === "complete" || s.phase === "welcome") return;
    setInputValue("");
    go(v);
  };

  const handleReset = () => {
    setMessages([]); setOpts([]); setDone(false); setInputValue("");
    Object.assign(s, {
      phase: "welcome",
      contact: { name: "", dob: "", address: "", email: "", phone: "", marital: "", spouse_name: "", spouse_dob: "", spouse_contact: "" },
      selectedCategory: null, completedCategories: [], questions: [], qIdx: 0,
      answers: {}, allAnswers: {}, repeatCounts: {}, repeatIter: {}, groupRefs: {},
    });
    loadConfig();
    setTimeout(() => {
      setMessages([{ type: "bot", text: "Hi! I'm the A2Insure Digital Assistant." }]);
      setTimeout(() => {
        s.phase = "intent";
        setMessages((p) => [...p, {
          type: "bot",
          text: "Are you looking for a new quote today, or do you need help with an existing policy?"
        }]);
        setOpts([
          { label: "Get a New Quote", value: "quote" },
          { label: "Policy Help", value: "service" },
        ]);
      }, 400);
    }, 200);
  };

  const darkBg = dark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-200";
  const darkInput = dark ? "bg-slate-800 text-white placeholder-slate-400 border-slate-700" : "bg-slate-100 text-slate-800 placeholder-slate-400";

  // Hide chatbot on admin page
  if (location.pathname === "/admin") return null;

  return (
    <div className="chatbot-float">
      {open && (
        <div data-testid="chatbot-window"
          className={`mb-3 w-[340px] sm:w-[380px] rounded-3xl shadow-2xl border overflow-hidden animate-fade-up flex flex-col ${dark ? "bg-slate-950 border-slate-800" : "bg-slate-50 border-slate-200"}`}
          style={{ maxHeight: "min(560px, calc(100vh - 90px))" }}>
          <div className={`px-4 py-3 flex items-center justify-between rounded-t-3xl border-b ${darkBg}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-900 rounded-2xl flex items-center justify-center text-white shadow-lg shrink-0">
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <h1 className={`font-extrabold tracking-tight italic text-sm ${dark ? "text-white" : "text-slate-800"}`}>A2INSURE</h1>
                <p className="text-[9px] font-bold uppercase tracking-widest text-blue-600">Universal Underwriter</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="chatbot-dark-toggle" onClick={() => setDark(!dark)} className={`p-2 rounded-xl transition-colors ${dark ? "hover:bg-slate-800 text-slate-400" : "hover:bg-slate-100 text-slate-500"}`}>
                {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <button data-testid="chatbot-close-btn" onClick={() => setOpen(false)} className={`p-2 rounded-xl transition-colors ${dark ? "hover:bg-slate-800 text-slate-400" : "hover:bg-slate-100 text-slate-500"}`}>
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className={`flex-1 overflow-y-auto p-4 space-y-4 chatbot-messages ${dark ? "bg-slate-900" : "bg-white"}`} style={{ minHeight: 0 }}>
            {messages.map((msg, i) =>
              msg.type === "bot" ? (
                <BotBubble key={i} text={msg.text} options={i === messages.length - 1 ? opts : []} onOption={(l, v) => go(v, l)} />
              ) : (
                <UserBubble key={i} text={msg.text} />
              )
            )}
            <div ref={endRef} />
          </div>
          <div className={`border-t px-4 py-3 ${darkBg}`}>
            {done ? (
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-emerald-500 font-bold text-xs uppercase tracking-tight mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                  Profile Transmitted to A2 Team
                </div>
                <button data-testid="chat-start-over-btn" onClick={handleReset} className="text-xs text-blue-600 font-semibold hover:underline">Start a new conversation &rarr;</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex gap-2">
                <input data-testid="chat-text-input" type="text" value={inputValue} onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type your answer here..." className={`flex-1 rounded-2xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all ${darkInput}`} />
                <button data-testid="chat-send-btn" type="submit" disabled={!inputValue.trim()}
                  className="bg-blue-900 hover:bg-blue-800 text-white p-3 rounded-2xl transition-all shadow-md disabled:opacity-40 shrink-0">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            )}
          </div>
        </div>
      )}
      <button data-testid="chatbot-toggle-btn" onClick={open ? () => setOpen(false) : handleOpen}
        className="w-14 h-14 bg-[#D4AF37] hover:bg-[#B5952F] text-[#002147] rounded-full shadow-lg flex items-center justify-center transition-all animate-pulse-gold"
        aria-label={open ? "Close chat" : "Open chat"}>
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </div>
  );
}
